import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Loader2, MapPin } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get("role") || "customer");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register({ ...form, role });
      toast.success("Account created!");
      navigate(user.role === "vendor" ? "/vendor" : "/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-[#E5F0F0] to-[#F4F7F7]" data-testid="register-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <div className="relative w-12 h-12 rounded-xl overflow-hidden shadow-md" style={{background: "linear-gradient(135deg, #00B7EB 0%, #008FA4 55%, #006B85 100%)"}}>
            <div className="absolute inset-0 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" strokeWidth={2.4} />
            </div>
          </div>
          <div className="font-display text-2xl font-bold tracking-tight" style={{color: "var(--brand-primary, #054F63)"}}>{settings.site_name || "HyderabadTourism.in"}</div>
        </Link>

        <div className="bg-white rounded-2xl border border-border p-8 shadow-sm">
          <h1 className="font-display text-3xl mb-2">Create your account</h1>
          <p className="text-muted-foreground text-sm mb-6">Join 10,000+ happy travellers</p>

          <Tabs value={role} onValueChange={setRole} className="mb-5">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="customer" data-testid="role-customer">Customer</TabsTrigger>
              <TabsTrigger value="vendor" data-testid="role-vendor">Vendor</TabsTrigger>
              <TabsTrigger value="agent" data-testid="role-agent">Travel Agent</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="reg-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="reg-email" />
            </div>
            <div>
              <Label htmlFor="phone">Mobile</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 ..." data-testid="reg-phone" />
            </div>
            <div>
              <Label htmlFor="pwd">Password</Label>
              <Input id="pwd" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="reg-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-saffron hover:bg-saffron-dark text-white py-6 text-base" data-testid="reg-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-teal font-semibold hover:underline" data-testid="reg-login-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
